import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#49ffc1' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#49ffc1' },
};

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/forgot-password`,
        { email: email.trim() },
        { headers: { 'ngrok-skip-browser-warning': '1' } }
      );
      setSuccess(
        response.data?.message ||
          'Si un compte administrateur, coach ou entreprise est associé à cette adresse, un email de réinitialisation a été envoyé.'
      );
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Impossible d’envoyer l’email de réinitialisation. Réessayez plus tard.';
      setError(typeof message === 'string' ? message : 'Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            bgcolor: '#281e30',
          }}
        >
          <Typography component="h1" variant="h4" gutterBottom sx={{ color: '#fff' }} fontWeight="bold">
            Mot de passe oublié
          </Typography>
          <Typography variant="body1" sx={{ color: '#fff', opacity: 0.8, mb: 2, textAlign: 'center' }}>
            Saisissez votre adresse e-mail. Si un compte admin, coach ou entreprise y est associé, vous recevrez un lien de réinitialisation.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Adresse e-mail"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading || Boolean(success)}
              sx={fieldSx}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                bgcolor: '#49ffc1',
                color: '#281e30',
                fontWeight: 700,
                '&:hover': { bgcolor: '#3de6ad' },
              }}
              disabled={loading || Boolean(success)}
            >
              {loading ? <CircularProgress size={24} sx={{ color: '#281e30' }} /> : 'Envoyer le lien'}
            </Button>
          </Box>

          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => navigate('/login')}
            sx={{ color: '#49ffc1', mt: 1 }}
          >
            Retour à la connexion
          </Link>
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPassword;
